import * as React from 'react';

import {
    Accordion,
    Breadcrumb,
    Button,
    Heading,
    List,
    Pagination,
    Tabs,
    Text
} from '@/components/ui';

import { createFamily, PreviewRow, PreviewStack, VariantPreview, withStorySources } from './helpers';

const BREADCRUMB_TRAIL = [
    { label: 'Components', href: '/design_system#' },
    { label: 'Forms', href: '/design_system#' },
    { label: 'Inputs', href: '/design_system#' },
    { label: 'Select', href: '/design_system#' },
    { label: 'Variants' }
];

const AccordionHero = () => {
    const [value, setValue] = React.useState('');

    const items = [
        { id: 'overview', title: 'Overview', content: <Text variant="body">High-level summary of the topic — short paragraph or two introducing what the section covers.</Text> },
        { id: 'variants', title: 'Variants', content: <Text variant="body">Detail on each variant. Content can be any ReactNode — paragraphs, lists, tables, forms, anything.</Text> },
        { id: 'guidelines', title: 'Guidelines', content: <Text variant="body">When to reach for this primitive vs. alternatives, plus accessibility expectations.</Text> }
    ];

    return (
        <Accordion items={items} onValueChange={setValue} value={value}/>
    );
};

const BreadcrumbStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'maxItems', value: 'unset' }]}>
            <Breadcrumb items={BREADCRUMB_TRAIL}/>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'maxItems', value: '4' }, { label: 'click "…"', value: 'expand' }]}>
            <Breadcrumb items={BREADCRUMB_TRAIL} maxItems={4}/>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'maxItems', value: '3' }, { label: 'click "…"', value: 'expand' }]}>
            <Breadcrumb items={BREADCRUMB_TRAIL} maxItems={3}/>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'maxItems', value: '2' }, { label: 'click "…"', value: 'expand' }]}>
            <Breadcrumb items={BREADCRUMB_TRAIL} maxItems={2}/>
        </VariantPreview>
    </PreviewStack>
);

const BreadcrumbHero = () => (
    <Breadcrumb
        items={[
            { label: 'Components', href: '/design_system#' },
            { label: 'Forms', href: '/design_system#' },
            { label: 'Inputs', href: '/design_system#' },
            { label: 'Select', href: '/design_system#' },
            { label: 'Variants' }
        ]}
        maxItems={3}
    />
);

const PaginationHero = () => {
    const [page, setPage] = React.useState(500);

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-4">
            <Pagination currentPage={page} onChange={setPage} totalPages={1000} />
            <Text tone="muted" variant="body">
                Page <Text as="span" tone="foreground" variant="control">{page}</Text> of 1000
            </Text>
        </div>
    );
};

const TabsHero = () => (
    <Tabs
        items={[
            { value: 'overview', label: 'Overview', content: <Text variant="body">High-level usage guidance and default composition.</Text> },
            { value: 'api', label: 'API', content: <Text variant="body">Props and related subcomponents for this family.</Text> },
            { value: 'tokens', label: 'Tokens', content: <Text variant="body">Spacing, colors and interaction states.</Text> }
        ]}
    />
);

const ListHero = () => {
    const items = [
        { value: 'active-power', label: 'Active power', description: 'Lisbon HQ · Electric', leadingIcon: 'bolt', trailingIcon: 'chevronRight' },
        { value: 'energy-consumption', label: 'Energy consumption', description: 'Porto Plant · Electric', leadingIcon: 'chartLine', trailingIcon: 'chevronRight' },
        { value: 'boiler-temperature', label: 'Boiler temperature', description: 'Lisbon HQ · Thermal', leadingIcon: 'water', trailingIcon: 'chevronRight' }
    ];
    const [picked, setPicked] = React.useState(null);

    return (
        <div className="tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-border tw-bg-background">
            <List items={items} onSelect={setPicked}/>
            {picked ? (
                <div className="tw-border-t tw-border-border tw-px-3 tw-py-2">
                    <Text tone="muted" variant="body-sm">
                        Picked: <Text as="span" tone="foreground" variant="control">{picked}</Text>
                    </Text>
                </div>
            ) : null}
        </div>
    );
};

const navigationStorySections = [
    createFamily({
        id: 'accordion',
        label: 'Accordion',
        description: 'Stacked disclosure sections. Closed primitive: caller passes `items` (`{ id, title, content, disabled? }`) and the controlled `value` / `onValueChange`. Single-mode by default; opt into independent open/close per item with `multiple`.',
        subcomponents: ['Accordion'],
        stories: [{ id: 'accordion-basic', title: 'Expandable sections', hero: AccordionHero }]
    }),
    createFamily({
        id: 'breadcrumb',
        label: 'Breadcrumb',
        description: 'Hierarchy navigation. Closed primitive: items array is the trail; an item without `href` is the current page (`aria-current`). Optional `maxItems` caps the visible count — longer trails compact to `[first segment, …, last segment]` and clicking the ellipsis expands inline.',
        subcomponents: ['Breadcrumb'],
        stories: [{ id: 'breadcrumb-basic', title: 'Navigation trail', render: BreadcrumbStory, hero: BreadcrumbHero }]
    }),
    createFamily({
        id: 'list',
        label: 'List',
        description: 'Closed list primitive — flush rows for menus, search results and dense pickers. Caller passes `items` (`{ value, label, description?, leadingIcon?, trailingIcon? }`) and an `onSelect(value)` handler. Rows have no per-row rounding or inter-row gap; caller wraps in their own rounded container if needed.',
        subcomponents: ['List'],
        stories: [{ id: 'list-basic', title: 'Title, description and onSelect', hero: ListHero }]
    }),
    createFamily({
        id: 'pagination',
        label: 'Pagination',
        description: 'Pagination controls for list and table navigation.',
        subcomponents: ['Pagination'],
        stories: [{ id: 'pagination-basic', title: 'Pagination controls', hero: PaginationHero }]
    }),
    createFamily({
        id: 'tabs',
        label: 'Tabs',
        description: 'Tabbed navigation for small content panes.',
        subcomponents: ['Tabs'],
        stories: [{ id: 'tabs-basic', title: 'Tab panels', hero: TabsHero }]
    })
].map(withStorySources);

export { navigationStorySections };
